import { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";

const SCROLL_DELTA = 6;
const HIDE_AFTER = 100;
const BOTTOM_LOCK = 8;
// بعد از هر تغییر مسیر، اسکرول‌هایی که خود مرورگر/Next انجام می‌دهد (رفتن به بالا، بازگرداندن
// اسکرول در back/forward، جابه‌جایی محتوای در حال لود) ردیف دوم را دوباره نمی‌بندند
const NAVIGATION_SETTLE = 800;
const HOLD_EVENT = "jmt:navbar-hold";
const REVEAL_LOCK_EVENT = "jmt:navbar-reveal-lock";

/* اسکرول‌های برنامه‌ای (مثل فهرست مطالب) در طول حرکت ردیف دوم هدر را بسته
   نگه می‌دارند تا باز و بسته شدنش ارتفاع هدر را وسط اسکرول عوض نکند. */
export const holdNavbar = () =>
    window.dispatchEvent(new CustomEvent(HOLD_EVENT, { detail: true }));

export const releaseNavbar = () =>
    window.dispatchEvent(new CustomEvent(HOLD_EVENT, { detail: false }));

/* برخلاف hold، ردیف دوم را نمی‌بندد؛ فقط تا وقتی قفل است اسکرول به بالا آن را باز نمی‌کند
   (مثلا وقتی نشانگر روی تب‌های چسبان صفحه‌ی محصول است و نباید زیر ردیف دوم برود). */
export const lockNavbarReveal = (locked) =>
    window.dispatchEvent(new CustomEvent(REVEAL_LOCK_EVENT, { detail: locked === true }));

const useScrollDirection = () => {
    /* هدر بین صفحه‌ها mount می‌ماند؛ اگر ردیف دوم در صفحه‌ی قبل با اسکرول بسته شده بود، صفحه‌ی
       جدید هم بدون آن باز می‌شد تا کاربر دوباره اسکرول کند. برای همین به جای یک boolean مسیری
       که ردیف دوم در آن بسته شده نگه داشته می‌شود و در هر مسیر دیگری خودبه‌خود باز است.
       setState حین رندر (الگوی lastPathname) اینجا کار نمی‌کند: Next صفحه‌ی جدید را در یک
       transition رندر می‌کند که تا لود شدن صفحه چند بار suspend و دور ریخته می‌شود و آپدیت
       باز شدن ردیف دوم در commit نهایی گم می‌شد. */
    const pathname = usePathname();
    const pathnameRef = useRef(pathname);
    const [hiddenOn, setHiddenOn] = useState(null);
    const showNavbar = hiddenOn !== pathname;

    const lastScrollY = useRef(0);
    // null یعنی هنوز اندازه‌گیری نشده؛ اولین اسکرول آن را می‌گیرد
    const lastDocHeight = useRef(null);
    const frame = useRef(0);
    const held = useRef(false);
    const revealLocked = useRef(false);
    const navigatedAt = useRef(0);

    useEffect(() => {
        pathnameRef.current = pathname;
        navigatedAt.current = performance.now();
        lastScrollY.current = window.scrollY;
        // ارتفاع سند اینجا خوانده نمی‌شود (خواندن scrollHeight در افکت layout اجباری می‌ساخت)؛ اولین evaluate در
        // requestAnimationFrame آن را می‌گیرد و همان فریم را مثل «تغییر ارتفاع» نادیده می‌گیرد
        lastDocHeight.current = null;
        // ردیف دوم همین حالا هم باز است؛ فقط مسیر قبلی پاک می‌شود تا برگشت به آن بسته باز نشود
        setHiddenOn(null);
    }, [pathname]);

    useEffect(() => {
        lastScrollY.current = window.scrollY;
        lastDocHeight.current = null;

        const setShowNavbar = (show) => setHiddenOn(show ? null : pathnameRef.current);

        const evaluate = () => {
            frame.current = 0;

            const currentScrollY = window.scrollY;
            const docHeight = document.documentElement.scrollHeight;
            const delta = currentScrollY - lastScrollY.current;

            const height_changed = docHeight !== lastDocHeight.current;
            lastDocHeight.current = docHeight;

            const settling = performance.now() - navigatedAt.current < NAVIGATION_SETTLE;

            if (held.current || height_changed || settling) {
                lastScrollY.current = currentScrollY;
                return;
            }

            const at_bottom =
                currentScrollY + window.innerHeight >= docHeight - BOTTOM_LOCK;
            if (at_bottom) {
                lastScrollY.current = currentScrollY;
                return;
            }

            if (Math.abs(delta) < SCROLL_DELTA) return;

            /* پرش یک‌باره‌ی بزرگ‌تر از ارتفاع صفحه کار کاربر نیست: بازگرداندن اسکرول مرورگر بعد از
               ریلود یا پرش به #anchor. اگر حساب شود ردیف دوم هدر از همان ورود بسته می‌ماند. */
            if (Math.abs(delta) > window.innerHeight) {
                lastScrollY.current = currentScrollY;
                return;
            }

            lastScrollY.current = currentScrollY;
            const next = delta < 0 || currentScrollY <= HIDE_AFTER;
            if (next && revealLocked.current) return;
            setShowNavbar(next);
        };

        const handleScroll = () => {
            if (frame.current) return;
            frame.current = requestAnimationFrame(evaluate);
        };

        const handleHold = (event) => {
            held.current = event.detail === true;            lastScrollY.current = window.scrollY;
            if (held.current) setShowNavbar(false);
        };

        const handleRevealLock = (event) => {
            revealLocked.current = event.detail === true;
        };

        window.addEventListener("scroll", handleScroll, { passive: true });
        window.addEventListener(HOLD_EVENT, handleHold);
        window.addEventListener(REVEAL_LOCK_EVENT, handleRevealLock);
        return () => {
            window.removeEventListener("scroll", handleScroll);
            window.removeEventListener(HOLD_EVENT, handleHold);
            window.removeEventListener(REVEAL_LOCK_EVENT, handleRevealLock);
            if (frame.current) cancelAnimationFrame(frame.current);
        };
    }, []);

    return showNavbar;
};

export default useScrollDirection;
